import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'

import { mq, mqBreakpoints } from 'marketing-site/src/library/utils'
import { HeadingBase } from 'marketing-site/src/mvp/library/elements/HeadingBase'
import { Text } from 'marketing-site/src/library/elements/Text'
import { RichText } from 'marketing-site/src/mvp/library/elements/RichText'
import { IProps } from './index'

export const HeroHorizontal: React.FC<IProps> = ({
  layout,
  eyebrow,
  title,
  titleSize = 'heading-1',
  subhead,
  ctas,
  footnote,
  media,
  mediaType,
  backgroundColor = 'transparent',
  backgroundMedia,
  backgroundMediaPosition = 'top center',
  backgroundMediaSize = 'contain',
}) => {
  const [headingAlignment, setHeadingAlignment] = useState<'start' | 'center'>('start')

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        entry.target.clientWidth >= mqBreakpoints.desktop
          ? setHeadingAlignment('start')
          : setHeadingAlignment('center')
      }
    })
    resizeObserver.observe(document.body)
  }, [])

  const classes = classNames('hero-horizontal', 'grid-layout', {
    [`hero-horizontal--${layout}`]: layout,
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
  })

  const mediaClasses = classNames('hero-horizontal__media', {
    'hero-horizontal__media--inset': mediaType === 'image',
  })

  return (
    <div className={classes} data-testid="hero-horizontal">
      <div className="hero-horizontal__inner">
        <header className="hero-horizontal__header">
          <HeadingBase
            eyebrow={eyebrow}
            title={title}
            titleSize={titleSize}
            subhead={subhead}
            alignment={headingAlignment}
          />
          {ctas && <div className="hero-horizontal__ctas">{ctas}</div>}
          {footnote && (
            <div className="hero-horizontal__footnote">
              {
                <Text size="caption">
                  <RichText document={footnote} />
                </Text>
              }
            </div>
          )}
        </header>

        <div className={mediaClasses}>{media}</div>
      </div>

      {backgroundMedia && (
        <div className="hero-horizontal__background-media">{backgroundMedia}</div>
      )}

      <style jsx>
        {`
          .hero-horizontal {
            position: relative;
            padding-top: 2.5rem;

            &__inner {
              row-gap: 2.5rem;
              align-items: center;
              position: relative;
              z-index: 10;
            }

            &__header,
            &__media {
              grid-column: 1/-1;
            }

            &__header {
              text-align: center;
            }

            &__eyebrow {
              margin-bottom: 1.25rem;
            }

            &__subhead {
              margin-top: 1.5rem;

              :global(.rich-text > * + *) {
                margin-top: 1.25rem;
              }

              :global(.rich-text > ul li) {
                margin-bottom: 0.5rem;
              }

              :global(.rich-text > br:last-child) {
                display: none;
              }
            }

            &__ctas {
              display: flex;
              flex-direction: column;
              column-gap: 0.75rem;
              row-gap: 0.5rem;
              justify-content: center;
              margin-top: 2rem;

              & :global(form) {
                max-width: 27.5rem;
              }

              & > :global(.cta-link + .cta-link--link-only-blue),
              & > :global(.cta-link + .cta-link--link-only-black) {
                margin-top: 0.875rem;
              }
            }

            &__footnote {
              margin-top: 1.375rem;

              & :global(ul) {
                display: flex;
                flex-wrap: wrap;
                column-gap: 1.25rem;
                row-gap: 0.5rem;
                justify-content: center;
              }

              & :global(ul li) {
                margin-bottom: 0;
              }

              & :global(ul),
              & :global(ul li span) {
                font-size: 14px;
              }

              & :global(ul li p::before) {
                height: 1.5rem;
              }
            }

            &__media {
              border-radius: 1rem;
              overflow: hidden;

              & > :global(picture),
              & > :global(picture img),
              & > :global(.image-with-modal-video) {
                width: 100%;
              }
            }

            &__background-media {
              display: flex;
              justify-content: center;
              align-items: center;
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              left: 0;
              z-index: 0;

              & > :global(*) {
                width: 100%;
                max-width: 90rem;
                height: 100%;
              }

              & > :global(picture img) {
                width: 100%;
                height: 100%;
                object-position: ${backgroundMediaPosition};
                object-fit: ${backgroundMediaSize};
              }
            }
          }

          @media (${mq.tablet}) {
            .hero-horizontal {
              padding-top: 4rem;
              &__inner {
                row-gap: 3.5rem;
              }

              &__subhead {
                margin-top: 2rem;

                :global(.rich-text > * + *) {
                  margin-top: 1.5rem;
                }
              }

              &__ctas {
                flex-direction: row;
                align-items: center;
                margin-top: 2.5rem;

                & > :global(.cta-link + .cta-link--link-only-blue),
                & > :global(.cta-link + .cta-link--link-only-black) {
                  margin-top: 0;
                  margin-left: 0.75rem;
                }
              }

              &__subhead + &__footnote {
                margin-top: 2.5rem;
              }

              &__ctas + &__footnote {
                margin-top: 1rem;
              }

              &__media {
                grid-column: 1/-1;
                border-radius: 1.5rem;

                &--inset {
                  grid-column: 2/-2;
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .hero-horizontal {
              padding-top: 5rem;
              &__inner {
                grid-row-gap: 2.5rem;
              }

              &__header {
                grid-column: 1/7;
                grid-row: 1;
                text-align: left;
              }

              &__eyebrow {
                margin-bottom: 1.5rem;
              }

              &__ctas {
                justify-content: start;
              }

              &__media {
                grid-column: 7/-1;
                grid-row: 1;

                & > :global(picture) {
                  padding-left: 0;
                  padding-right: 0;
                }
              }

              &__footnote {
                & :global(ul) {
                  justify-content: start;
                }
              }

              &--media-right {
                .hero-horizontal__header {
                  padding-right: 2rem;
                }
              }

              &--media-left {
                .hero-horizontal__header {
                  grid-column: 7/-1;
                  padding-left: 2rem;
                }

                .hero-horizontal__media {
                  grid-column: 1/7;
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
